import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const FarmManagement = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-yellow-900/60 mix-blend-multiply z-10"></div>
                    <img
                        src={require('../../../assets/images/services/farm_management.png')}
                        alt="Farm Management"
                        className="w-full h-full object-cover opacity-60"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-thin mb-6"
                    >
                        Farm <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-600">Management</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Structured Farming. Sustainable Growth. Profitable Results.
                    </motion.p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-2/3"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            <span className="font-semibold text-gray-900">Farm Management</span> at Marvelous Agri Solution transforms farming from routine activity into a strategically managed agricultural enterprise.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            We design, monitor, and optimize every stage of farm operations to ensure efficiency, productivity, and long-term sustainability.
                        </p>
                        <p className="text-xl font-medium text-gray-800 italic border-l-4 border-yellow-500 pl-6 py-4 bg-yellow-50 rounded-r-2xl mb-10">
                            Because successful farming is not accidental — it is systematically managed.
                        </p>

                        <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-yellow-500 mb-10">
                            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                                🚀 Our Farm Management Approach
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                {[
                                    "Seasonal crop planning & scheduling",
                                    "Resource allocation & cost optimization",
                                    "Soil health monitoring",
                                    "Irrigation & nutrient management",
                                    "Pest & disease risk control",
                                    "Yield performance analysis"
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 text-gray-700">
                                        <CheckCircleIcon className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                                        <span className="font-medium text-gray-800">{item}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-yellow-800 font-medium italic mt-6 border-t border-gray-100 pt-6">
                                Every decision is aligned with productivity and profitability goals.
                            </p>
                        </div>

                        <div className="bg-gray-900 p-8 rounded-2xl shadow-xl text-white mb-10">
                            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-yellow-400">
                                📊 Operational Excellence
                            </h3>
                            <p className="text-gray-300 text-lg font-light mb-6">
                                We bring structure to farm operations by integrating:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                                {[
                                    "Scientific crop planning",
                                    "Data-driven monitoring",
                                    "Input efficiency management",
                                    "Risk mitigation strategies",
                                    "Market-oriented production planning"
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3 text-gray-300 font-light">
                                        <div className="bg-yellow-500/20 p-1.5 rounded-full flex-shrink-0">
                                            <div className="h-2 w-2 rounded-full bg-yellow-400"></div>
                                        </div>
                                        {item}
                                    </div>
                                ))}
                            </div>
                            <div className="bg-yellow-900/40 p-6 rounded-xl border border-yellow-700/50 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
                                <p className="text-lg text-yellow-100 font-light">The result?</p>
                                <div className="flex flex-wrap justify-center gap-2 text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
                                    <span>Higher efficiency.</span>
                                    <span>Lower wastage.</span>
                                    <span>Better returns.</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 p-8 rounded-2xl border border-yellow-200/60 shadow-sm relative overflow-hidden">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-yellow-200/40 rounded-full blur-3xl"></div>
                            <h3 className="text-2xl font-bold text-yellow-900 mb-4 relative z-10">🌱 From Field to Financial Performance</h3>
                            <p className="text-yellow-800 text-lg leading-relaxed font-light mb-6 relative z-10">
                                Farm Management is more than cultivation — it is agricultural business optimization.
                            </p>
                            <p className="text-yellow-800 text-lg leading-relaxed font-light mb-8 relative z-10">
                                At Marvelous Agri Solution, we ensure your farm operates like a well-designed system where every resource contributes to measurable growth.
                            </p>
                            <div className="flex flex-col md:flex-row justify-between gap-4 relative z-10 pt-6 border-t border-yellow-200">
                                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-orange-600 flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-500"></span> Planned Actions.
                                </p>
                                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-orange-600 flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-500"></span> Controlled Operations.
                                </p>
                                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-orange-600 flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-500"></span> Maximized Profitability.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="lg:w-1/3 sticky top-24"
                    >
                        <div className="bg-white p-2 rounded-2xl shadow-2xl skew-y-2 transform hover:skew-y-0 transition-transform duration-500">
                            <img
                                src={require('../../../assets/images/services/farm_management.png')}
                                alt="Farm Management"
                                className="rounded-xl w-full h-auto object-cover"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default FarmManagement;
